/**
 * FCI draw-request payload viewer (budget-scope-of-work-draws.md §4).
 *
 * GET .../draws/{dealId}/{drawId}/fci_payload → { rest: {method, url, formdata},
 * graphql: {query} }. This is a BUILDER ONLY — the LOS never posts to FCI; the
 * operator copies the payload and fills the still-missing `investorAccountNumber`
 * (comes back null) before pasting into FCI.
 */
import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";

const CodeBlock = ({ label, value }) => {
  const { enqueueSnackbar } = useSnackbar();
  const text =
    typeof value === "string" ? value : JSON.stringify(value, null, 2);
  const copy = useCallback(() => {
    navigator.clipboard?.writeText(text).then(
      () => enqueueSnackbar("Copied", { variant: "success" }),
      () => enqueueSnackbar("Could not copy", { variant: "error" }),
    );
  }, [text, enqueueSnackbar]);

  return (
    <Box sx={{ mb: 2 }}>
      <Flx jb ac sx={{ mb: 0.5 }}>
        <Txt
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: "uppercase",
            color: "#64748b",
          }}
        >
          {label}
        </Txt>
        <Button
          variant="text"
          size="small"
          startIcon={<ContentCopyRounded sx={{ fontSize: 12 }} />}
          onClick={copy}
          sx={{ textTransform: "none", fontSize: 12 }}
        >
          Copy
        </Button>
      </Flx>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          bgcolor: "#0f172a",
          color: "#e2e8f0",
          borderRadius: "8px",
          fontSize: 12,
          fontFamily: "ui-monospace, monospace",
          lineHeight: 1.5,
          overflowX: "auto",
          whiteSpace: "pre",
        }}
      >
        {text}
      </Box>
    </Box>
  );
};

const FciPayloadDialog = ({ open, onClose, dealId, draw, fetchPayload }) => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !draw?._id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPayload(null);
    fetchPayload(dealId, draw._id || draw.oid)
      .then((data) => !cancelled && setPayload(data))
      .catch(
        (err) =>
          !cancelled &&
          setError(
            err?.response?.data?.detail ||
              err?.message ||
              "Could not build the FCI payload.",
          ),
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, draw, dealId, fetchPayload]);

  const investorMissing = payload?.rest?.formdata
    ? /investorAccountNumber["\s:=]+null/i.test(
        JSON.stringify(payload.rest.formdata),
      )
    : false;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 600 }}>
        FCI draw-request payload
        {draw?.drawNumber != null ? ` · Draw ${draw.drawNumber}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        <Txt sx={{ fontSize: 12, color: "#64748b", display: "block", mb: 2 }}>
          Builder only. The LOS never posts to FCI. Copy the payload below and
          fill any missing fields before pasting into FCI.
          {investorMissing &&
            " Note: investorAccountNumber isn't tracked on the deal yet, so fill it in."}
        </Txt>

        {loading ? (
          <Flx ac jc sx={{ py: 5 }}>
            <CircularProgress size={22} />
          </Flx>
        ) : error ? (
          <Txt sx={{ fontSize: 12, color: "#b91c1c" }}>{error}</Txt>
        ) : payload ? (
          <>
            {payload.rest && (
              <CodeBlock label="REST request" value={payload.rest} />
            )}
            {payload.graphql?.query && (
              <CodeBlock label="GraphQL query" value={payload.graphql.query} />
            )}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default FciPayloadDialog;
